import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CSVExports } from '@vendasta/sales-opportunities';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ExportCsvNotificationDialogComponent } from '../export-csv-notification-dialog/export-csv-notification-dialog.component';
import { PipelineExportCsvService } from '../pipeline-export-csv/pipeline-export-csv.service';

const requireOneValidator: ValidatorFn = (formGroup: FormGroup): ValidationErrors | null => {
  const controls = formGroup.controls;

  // Check if at least one control is filled in
  const isOneControlFilled = Object.values(controls).some((control: FormControl) => !!control.value);

  // Return validation error if the condition is not met
  return isOneControlFilled ? null : { requireOneFilled: true };
};

interface ExportForm {
  opportunities: FormControl<boolean>;
  history: FormControl<boolean>;
}

@Component({
  selector: 'sales-ui-export-csv-selection-dialog',
  templateUrl: './export-csv-selection-dialog.component.html',
  styleUrls: ['./export-csv-selection-dialog.component.scss'],
})
export class ExportCsvSelectionDialogComponent {
  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  protected readonly isLoading$ = this.isLoading$$;

  protected readonly exportForm = this.formBuilder.group<ExportForm>(
    {
      opportunities: new FormControl(false),
      history: new FormControl(false),
    },
    { validators: requireOneValidator },
  );

  constructor(
    public dialogRef: MatDialogRef<ExportCsvSelectionDialogComponent>,
    private readonly i18n: TranslateService,
    private readonly pipelineExportCsvService: PipelineExportCsvService,
    private readonly alertService: SnackbarService,
    private readonly dialog: MatDialog,
    private readonly formBuilder: FormBuilder,
  ) {}

  closeDialog(): void {
    this.dialogRef.close();
  }

  async onSubmit(): Promise<void> {
    if (this.exportForm.valid) {
      this.isLoading$$.next(true);
      const i18nString = await firstValueFrom(this.i18n.stream('FRONTEND.SALES_UI.PIPELINE.CSV_EXPORT_ERROR'));
      const req = new CSVExports({
        opportunities: this.exportForm.value.opportunities,
        opportunityHistory: this.exportForm.value.history,
      });
      firstValueFrom(this.pipelineExportCsvService.initiateCSVExport(req))
        .then(() => {
          this.dialogRef.close();
          this.openCSVNotificationDialog();
        })
        .catch(() => {
          this.alertService.openErrorSnack(i18nString);
        })
        .finally(() => {
          this.isLoading$$.next(false);
        });
    }
  }

  private openCSVNotificationDialog(): void {
    this.dialog.open(ExportCsvNotificationDialogComponent, {
      width: '350px',
    });
  }
}
